import axios from 'axios';
import { store } from '../redux/store';

export enum AddressServiceLanguage {
  NL = 'NL',
  DE = 'DE',
  EN = 'EN',
  FR = 'FR',
  PT = 'PT',
}

export type AutoCompleteCitiesProps = {
  countryISO: string;
  language: AddressServiceLanguage;
  limit?: number;
  key: string;
};

export type AutoCompleteCitiesResponse = {
  Cities: Array<{
    CityId: number;
    PostalCode: string;
    Name: string;
  }>;
  Information: {
    EncounteredError: boolean;
    Messages: Array<{
      Code: string;
      MessageText: string;
      ErrorType: number;
    }>;
  };
};

export type AutoCompleteStreetsProps = {
  countryISO: string;
  language: AddressServiceLanguage;
  postalCode: string;
  limit?: number;
  key: string;
};

export type AutoCompleteStreetsResponse = {
  Streets: Array<{
    Name: string;
    StreetId: number;
  }>;
  Information: {
    EncounteredError: boolean;
    Messages: Array<{
      Code: string;
      MessageText: string;
      ErrorType: number;
    }>;
  };
};

export type ValidateAddressProps = {
  addressToValidate: {
    street: {
      name: string;
    };
    city: {
      name: string;
      postalCode: string;
    };
    houseNumber: string;
    countryISOCode: string;
  };
};

export type ValidateAddressResponse = {
  ValidationResult: {
    IsValid: boolean;
    ValidationPerField: Array<{
      Key: 'city' | 'street' | 'postalCode' | 'country' | 'number';
      Value: boolean;
    }>;
    ErrorMessage: string | null;
    Information: {
      EncounteredError: boolean;
      Messages: Array<{
        Code: string;
        MessageText: string;
        ErrorType: number;
      }>;
    };
  };
  Information: {
    EncounteredError: boolean;
    Messages: Array<{
      Code: string;
      MessageText: string;
      ErrorType: number;
    }>;
  };
};

export type GetCountriesProps = {
  language: AddressServiceLanguage;
};

export type GetCountriesResponse = [
  {
    IsoCode: string;
    Name: string;
  },
];

export type ResolveDutchAddressProps = {
  postCode: string;
  houseNumber: number;
};

export type ResolveDutchAddressResponse = {
  Address: {
    Street: {
      Name: string;
    };
  };
};

export interface IAddressService {
  autoCompleteCities(props: AutoCompleteCitiesProps): Promise<AutoCompleteCitiesResponse>;

  autoCompleteStreets(props: AutoCompleteStreetsProps): Promise<AutoCompleteStreetsResponse>;

  validateAddress(props: ValidateAddressProps): Promise<ValidateAddressResponse>;

  getCountries(props: GetCountriesProps): Promise<GetCountriesResponse>;

  resolveStreetFromPostcode(props: ResolveDutchAddressProps): Promise<ResolveDutchAddressResponse>;
}

class AddressService implements IAddressService {
  private getApiConfig = () => store.getState().apiConfig;

  async autoCompleteCities(props: AutoCompleteCitiesProps): Promise<AutoCompleteCitiesResponse> {
    const { countryISO, ...requestBody } = props;
    const response = await axios.post<AutoCompleteCitiesResponse>(
      `${this.getApiConfig().apiAddressService.apiRoot}/Address/AutoComplete/Cities/${countryISO}?api-version=2.0`,
      {
        ...requestBody,
      }
    );
    return response.data;
  }

  async autoCompleteStreets(props: AutoCompleteStreetsProps): Promise<AutoCompleteStreetsResponse> {
    const { countryISO, ...requestBody } = props;
    const response = await axios.post<AutoCompleteStreetsResponse>(
      `${this.getApiConfig().apiAddressService.apiRoot}/Address/AutoComplete/Streets/${countryISO}?api-version=2.0`,
      {
        ...requestBody,
      }
    );
    return response.data;
  }

  async validateAddress(props: ValidateAddressProps): Promise<ValidateAddressResponse> {
    const response = await axios.post<ValidateAddressResponse>(
      `${this.getApiConfig().apiAddressService.apiRoot}/Address/Validate?api-version=1.1`,
      {
        ...props,
      }
    );
    return response.data;
  }

  async getCountries(props: GetCountriesProps): Promise<GetCountriesResponse> {
    const response = await axios.get<GetCountriesResponse>(
      `${this.getApiConfig().apiAddressService.apiRoot}/Countries/Language/${props.language}?api-version=2.0`
    );
    return response.data;
  }

  async resolveStreetFromPostcode(
    props: ResolveDutchAddressProps
  ): Promise<ResolveDutchAddressResponse> {
    const response = await axios.post<ResolveDutchAddressResponse>(
      `${this.getApiConfig().apiAddressService.apiRoot}/Address/ResolveDutchAddress?api-version=1.0`,
      { houseNumber: props.houseNumber, postalCode: props.postCode }
    );
    return response.data;
  }
}

export const addressService = new AddressService();
